import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircleTwoTone, InfoCircleOutlined } from '@ant-design/icons';
import { Space, Tabs } from 'antd';
import DisplayBGV from './User_BGV';
import DisplayDrugTest from './User_DrugTest';
import DisplayAuthorization from './User_Authorization';
import DisplaySkillRepository from './User_SkillRepository';
import DisplayVDICompliance from './User_VDICompliance';
import { useParams } from 'react-router-dom';
import { fetchEmployeeData } from '../../util/documentConfig';
import { useSelector } from "react-redux";
import './UserView.css';

const UserView = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [userId, setUserId] = useState(null); // Store userId in state

 const { id } = useParams(); // Get userId from URL if available
      const email = localStorage.getItem("userEmail");
   const token = useSelector((state) => state.auth.token);

   useEffect(() => {
             const roleUser = JSON.parse(localStorage.getItem("userRoles"));
             
             if (roleUser && roleUser.includes("ADMINUSER")) {
                 // alert("setAdmin");
                  setIsAdmin(true);
                }
     
             // If userId is not available, fetch it using email
             if (!id) {
                 fetchData();
             } else {
                 setUserId(id); // Set from URL params
             }
         }, [id]); // Depend on id so it updates properly when URL changes
     
        
         const fetchData = async () => {
             try {
                 const fetchedUserId = await fetchEmployeeData(email);
                 console.log(`User ID: ${fetchedUserId}`);
                 setUserId(fetchedUserId);
             } catch (error) {
                 console.error("Failed to fetch userId:", error);
                 alert("Failed to fetch userId");
             }
         };

  // ✅ Centralized state to store status for all tabs
  const [statuses, setStatuses] = useState({
    '1': false,
    '2': false,
    '3': false,
    '4': false,
    '5': false
  });

  // ✅ Load stored status from localStorage on mount
  useEffect(() => {
    const loadStatus = () => {     
      setStatuses({
        '1': localStorage.getItem(`bgvFile_${userId}`) === 'true',
        '2': localStorage.getItem(`drugFile_${userId}`) === 'true',
        '3': localStorage.getItem(`authFile_${userId}`) === 'true',
        '4': localStorage.getItem(`skillRepo_${userId}`) === 'true',
        '5': localStorage.getItem(`vdi_${userId}`) === 'true'
      });
    };

    loadStatus(); // ✅ Load status when component mounts
  }, [userId]);

  // ✅ Function to update tab status (prevents unnecessary state updates)
  const updateTabStatus = useCallback((keyToUpdate, isCompleted) => {
    setStatuses((prevStatuses) => {
      if (prevStatuses[keyToUpdate] === isCompleted) return prevStatuses; // 🚨 Avoid unnecessary updates

      const updatedStatuses = {
        ...prevStatuses,
        [keyToUpdate]: isCompleted
      };

      // ✅ Persist status in localStorage
      switch (keyToUpdate) {
        case '1':
          localStorage.setItem(`bgvFile_${userId}`, isCompleted ? 'true' : '');
          break;
        case '2':
          localStorage.setItem(`drugFile_${userId}`, isCompleted ? 'true' : '');
          break;
        case '3':
          localStorage.setItem(`authFile_${userId}`, isCompleted ? 'true' : '');
          break;
        case '4':
          localStorage.setItem(`skillRepo_${userId}`, isCompleted ? 'true' : '');
          break;
        case '5':
          localStorage.setItem(`vdi_${userId}`, isCompleted ? 'true' : '');
          break;
        default:
          break;
      }

      console.log(`Updated status for key ${keyToUpdate}: ${isCompleted}`);
      return updatedStatuses;
    });
  }, [userId]);

  // ✅ Memoized callback functions to prevent unnecessary re-renders
  const handleBGVUploadSuccess = useCallback(
    (status) => updateTabStatus('1', status),
    [updateTabStatus]
  );

  const handleDrugTestUploadSuccess = useCallback(
    (status) => updateTabStatus('2', status),
    [updateTabStatus],
   
  );

  const handleAuthorizationUploadSuccess = useCallback(
    (status) => updateTabStatus('3', status),
    [updateTabStatus]
  );

  const handleSkillRepositoryCompletion = useCallback(
    (status) => updateTabStatus('4', status),
    [updateTabStatus]
  );

  const handleVDIComplianceCompletion = useCallback(
    (status) => updateTabStatus('5', status),
    [updateTabStatus]
  );

  // ✅ Tab items with dynamic status icons
  const items = [
    {
      key: '1',
      label: (
        <Space>
          BGV Form
          {statuses['1'] ? (
            <CheckCircleTwoTone twoToneColor="#52c41a" />
          ) : (
            <InfoCircleOutlined style={{ color: '#ffb700', transform: 'rotate(180deg)' }} />
          )}
        </Space>
      ),
      children: <DisplayBGV onFileUploadSuccess={handleBGVUploadSuccess} />
    },
    {
      key: '2',
      label: (
        <Space>
          Drug Test
          {statuses['2'] ? (
            <CheckCircleTwoTone twoToneColor="#52c41a" />
          ) : (
            <InfoCircleOutlined style={{ color: '#ffb700', transform: 'rotate(180deg)' }} />
          )}
        </Space>
      ),
      children: <DisplayDrugTest onFileUploadSuccess={handleDrugTestUploadSuccess} />
    },
    {
      key: '3',
      label: (
        <Space>
          Authorization Letter
          {statuses['3'] ? (
            <CheckCircleTwoTone twoToneColor="#52c41a" />
          ) : (
            <InfoCircleOutlined style={{ color: '#ffb700', transform: 'rotate(180deg)' }} />
          )}
        </Space>
      ),
      children: <DisplayAuthorization onFileUploadSuccess={handleAuthorizationUploadSuccess} />
    },
    {
      key: '4',
      label: (
        <Space>
          Skill Repository
          {statuses['4'] ? (
            <CheckCircleTwoTone twoToneColor="#52c41a" />
          ) : (
            <InfoCircleOutlined style={{ color: '#ffb700', transform: 'rotate(180deg)' }} />
          )}
        </Space>
      ),
      children: <DisplaySkillRepository onCompletion={handleSkillRepositoryCompletion} />
    },
    {
      key: '5',
      label: (
        <Space>
          VDI Compliance
          {statuses['5'] ? (
            <CheckCircleTwoTone twoToneColor="#52c41a" />
          ) : (
            <InfoCircleOutlined style={{ color: '#ffb700', transform: 'rotate(180deg)' }} />
          )}
        </Space>
      ),
      children: <DisplayVDICompliance onCompletion={handleVDIComplianceCompletion} />
    }
  ];

  return (
    <div>
      <div className="custom-tabs">
        <Tabs defaultActiveKey="1" type="card" items={items} />
      </div>
    </div>
  );
};

export default UserView;
